import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { API, Pagination, UserDTO } from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { ErrorService } from '../../../services/error.service';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'sb-comp-discussion-mentions',
  templateUrl: './sb-discussion-mentions.component.html',
  styleUrls: ['./sb-discussion-mentions.component.scss'],
  standalone: false,
})
export class SbDiscussionMentionsComponent implements OnInit, OnDestroy {
  activeIndex: number = 0;
  @Input() permissionName: string;
  @Input() searchStatus: boolean;
  @Output() searchStatusChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() userSelect: EventEmitter<UserDTO> = new EventEmitter<UserDTO>();
  @Output() cancelEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  users: UserDTO[];

  pagination: BehaviorSubject<Pagination>;

  debounceTimer: ReturnType<typeof setTimeout> = null;
  private listenerKeyboardEvents: any;

  constructor(
    private api: API,
    public util: UtilService,
    private errorHandler: ErrorService
  ) {
    this.searchStr = '';
  }

  _keyboardEvent: KeyboardEvent;

  get keyboardEvent(): KeyboardEvent {
    return this._keyboardEvent;
  }

  @Input() set keyboardEvent(val: KeyboardEvent) {
    this._keyboardEvent = val;
    this.invokeAutoSuggest(this._keyboardEvent);
  }

  _searchStr: string;

  get searchStr(): string {
    return this._searchStr;
  }

  @Input() set searchStr(val: string) {
    this._searchStr = val;
    this.requestDebounced();
  }

  public invokeAutoSuggest(ev: KeyboardEvent) {
    if (ev) {
      if (ev.defaultPrevented) {
        return; // Do nothing if the event was already processed
      }
      if ('ArrowUp' === ev.key) {
        ev.preventDefault();
        return this.prevActiveMatch();
      }
      if ('ArrowDown' === ev.key) {
        ev.preventDefault();
        return this.nextActiveMatch();
      }
      if ('Enter' === ev.key) {
        ev.preventDefault();
        this.selectUser(ev, this.users[this.activeIndex]);
      }
      if ('Escape' === ev.key) {
        ev.preventDefault();
        this.cancel();
      }
    }
  }

  cancel() {
    this.searchStatus = false;
    this.searchStatusChange.emit(this.searchStatus);
    this.cancelEmitter.emit(true);
  }

  nextActiveMatch() {
    this.activeIndex = this.activeIndex < this.users.length - 1 ? ++this.activeIndex : this.activeIndex;
  }

  prevActiveMatch() {
    this.activeIndex = this.activeIndex > 0 ? --this.activeIndex : 0;
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  ngOnInit(): void {
    this.requestDebounced();
    this.listenerKeyboardEvents = ev => {
      this.invokeAutoSuggest(ev);
    };
    this.pagination = new BehaviorSubject<Pagination>({
      page: 1,
      count: 99,
      pages: -1,
      resultcount: -1,
    });
    window.addEventListener('keydown', this.listenerKeyboardEvents, { passive: true });
  }

  refreshFromServer() {
    this.searchStatus = true;
    this.searchStatusChange.emit(this.searchStatus);
    this.api
      .shared_Discussion_retrieveUsersByPermissionName(
        this.permissionName,
        {
          search: this.util.encodeSearchQueryParam(false, this.searchStr),
        },
        this.pagination
      )
      .subscribe({
        next: data => {
          this.users = data;
          if (this.activeIndex > this.users.length) {
            this.activeIndex = 0;
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  selectUser($event, user: UserDTO) {
    $event.stopPropagation();
    $event.preventDefault();
    this.searchStatus = false;
    this.searchStatusChange.emit(this.searchStatus);
    this.userSelect.emit(user);
  }

  ngOnDestroy(): void {
    window.removeEventListener('keydown', this.listenerKeyboardEvents);
  }
}
